/**
 * useFuzzTap - lets the sandbox's fuzzer read this client's table.
 *
 * In emulator builds only, registers a reader with diagnostics/fuzzTap
 * that snapshots the shell's latest roster, turn tracker, open defence
 * prompts, shared log, tokens, frame, clocks and board (engine/fuzz/snapshot), and the shown plates measured. The shell is
 * read through a ref at call time, so the snapshot is always this
 * render's; nothing re-registers per render. Beside it, `window.__e2eUndoDepth` reads the shared undo stack's
 * pointer. In production the effect
 * returns at once and the reader is never installed.
 *
 * @param {object} shell - the composition root's scope (DeathWatchSimulator)
 */
import { useEffect, useRef } from 'react';
import { setFuzzTap } from '../diagnostics/fuzzTap';
import { tableSnapshot } from '../engine/fuzz/snapshot';
import { squadView } from '../engine/fuzz/squad';
import { readBeatLog } from '../diagnostics/beatLog';
import { readPlateLog } from '../diagnostics/plateLog';
import { measurePlateLayout } from '../diagnostics/plateLayout';

/** The GM's hidden figures as the leak oracle needs them; a reserved one (no combatant yet) has no name. */
const hiddenOf = (figures) =>
  [...(figures?.values() ?? [])].map((f) => ({ id: f.id, name: f.combatant?.name ?? '', bornHidden: f.bornHidden }));

/** The snapshot of one shell scope. */
function snapshotOf(s) {
  return tableSnapshot({
    seat: { sessionId: s.sessionId ?? null, playerId: s.playerId ?? null, isGm: !!s.isGm, actorId: s.actorId ?? null },
    // The SHARED table (plan map-display, G1): what every screen should agree on. The GM's hidden figures
    // and their tokens are the GM's alone, and listed apart for the leak oracle (engine/fuzz/hiddenLeak).
    roster: s.sharedRoster,
    trackerState: s.turnTracker?.trackerState,
    pendingDefences: s.pendingDefences,
    narrative: s.firestoreNarrative,
    tokens: s.sharedTokens,
    frame: s.direction?.frame ?? null,
    // The stage: which scene is on and the curtain - every screen must agree once the table is quiet (I).
    stage: s.stageSync?.stage ?? null,
    // The kill-teams: the GM's setup and the pools, which every screen must hold alike (engine/fuzz/squad).
    squad: s.killTeams ? squadView(s.killTeams.teams, s.killTeams.cohesion) : null,
    clocks: s.story?.clocks.clocks,
    board: s.story?.board.entries,
    asks: s.asks ? { incoming: s.asks.incoming, outgoing: s.asks.outgoing } : null,
    hidden: hiddenOf(s.hidden?.figures),
    narrativeTexts: (s.firestoreNarrative ?? []).map((e) => e.text ?? ''),
    beats: readBeatLog(),
    plates: readPlateLog(),
    // Measured as the fuzzer reads: whatever plates are on screen at that instant (engine/fuzz/plateLayout).
    plateLayout: measurePlateLayout(),
  });
}

export default function useFuzzTap(shell) {
  const latest = useRef(shell);
  latest.current = shell;
  useEffect(() => {
    if (import.meta.env.VITE_USE_EMULATOR !== 'true') return undefined;
    setFuzzTap(() => snapshotOf(latest.current));
    // The shared undo stack's pointer: Undo is enabled by any entry, so a spec that must undo exactly the one it
    // just made waits for the pointer to move (kill-team.spec; PocketBase lands the push later).
    window.__e2eUndoDepth = () => latest.current.sync?.sharedHistoryCacheRef?.current?.pointer ?? -1;
    return () => {
      setFuzzTap(null);
      delete window.__e2eUndoDepth;
    };
  }, []);
}
